<template>
  <div class="w-100 login-right pt-5">
    <div class="reg-form">
      <!-- Glass Card -->
      <div class="auth-card">
        <div class="brand">
          <div class="brand-dot"></div>
          <div class="brand-title">Создание аккаунта</div>
        </div>

        <!-- Step indicator -->
        <div class="steps">
          <div class="step" :class="{ active: step === 1, done: step > 1 }">
            <div class="step-badge">1</div>
            <div class="step-text">Данные</div>
          </div>
          <div class="step-line" :class="{ active: step > 1 }"></div>
          <div class="step" :class="{ active: step === 2 }">
            <div class="step-badge">2</div>
            <div class="step-text">Подтверждение</div>
          </div>
        </div>

        <!-- Alerts -->
        <transition name="fade">
          <div v-if="uiErrorList.length" class="alert-box alert-error alert-box-rich">
            <div class="alert-rich-head">
              <span class="alert-dot"></span>
              <span class="alert-rich-title">Проверьте данные</span>
            </div>
            <ul class="alert-error-list">
              <li v-for="(msg, idx) in uiErrorList" :key="'err-' + idx">{{ msg }}</li>
            </ul>
          </div>
          <div v-else-if="uiError" class="alert-box alert-error alert-box-rich">
            <div class="alert-rich-head">
              <span class="alert-dot"></span>
              <span>{{ uiError }}</span>
            </div>
          </div>
        </transition>

        <transition name="fade">
          <div v-if="uiSuccess" class="alert-box alert-success">
            <span class="alert-dot"></span>
            <span>{{ uiSuccess }}</span>
          </div>
        </transition>

        <!-- STEP 1: Registration -->
        <transition name="slide-fade" mode="out-in">
          <div v-if="step === 1" key="step1">
            <b-form @submit.prevent="handleRegister">
              <b-form-row>
                <b-col lg="12">
                  <label for="email" class="reg-label">{{ $t('regForm.email') }}</label>
                  <input
                    type="email"
                    id="email"
                    class="reg-input w-100 p-2"
                    required
                    v-model.trim="login.username"
                    :disabled="loading.register || loading.verify"
                    placeholder="you@example.com"
                    autocomplete="email"
                  />
                </b-col>
              </b-form-row>

              <b-form-row class="mt-3">
                <b-col lg="12">
                  <label for="password" class="reg-label">{{ $t('regForm.password') }}</label>
                  <div class="password-wrapper">
                    <input
                      :type="showPassword ? 'text' : 'password'"
                      id="password"
                      class="reg-input w-100 p-2"
                      :class="{ 'reg-input-active-hint': showPasswordHints }"
                      required
                      v-model="login.password"
                      :disabled="loading.register || loading.verify"
                      placeholder="••••••••"
                      autocomplete="new-password"
                      @focus="onPasswordFieldFocus"
                      @blur="onPasswordFieldBlur"
                      @input="onPasswordInput"
                    />
                    <button type="button" class="password-toggle" @click="showPassword = !showPassword" tabindex="-1">
                      <b-icon :icon="showPassword ? 'eye-slash' : 'eye'"></b-icon>
                    </button>
                  </div>
                </b-col>
              </b-form-row>

              <b-form-row class="mt-3">
                <b-col lg="12">
                  <label for="passwordRe" class="reg-label">{{ $t('regForm.passwordRe') }}</label>
                  <div class="password-wrapper">
                    <input
                      :type="showPasswordRe ? 'text' : 'password'"
                      id="passwordRe"
                      class="reg-input w-100 p-2"
                      :class="{ 'reg-input-active-hint': showPasswordHints }"
                      required
                      v-model="login.passwordRe"
                      :disabled="loading.register || loading.verify"
                      placeholder="••••••••"
                      autocomplete="new-password"
                      @focus="onPasswordFieldFocus"
                      @blur="onPasswordFieldBlur"
                      @input="onPasswordInput"
                    />
                    <button type="button" class="password-toggle" @click="showPasswordRe = !showPasswordRe" tabindex="-1">
                      <b-icon :icon="showPasswordRe ? 'eye-slash' : 'eye'"></b-icon>
                    </button>
                  </div>
                </b-col>
              </b-form-row>

              <!-- Live password requirements (all rules at once, updates as user types) -->
              <transition name="fade">
                <div v-if="showPasswordHints" class="password-hints" aria-live="polite">
                  <div class="password-hints-title">Требования к паролю</div>
                  <ul class="password-hints-list">
                    <li
                      v-for="row in passwordRequirementRows"
                      :key="row.key"
                      class="password-hint-row"
                      :class="row.stateClass"
                    >
                      <span class="password-hint-icon" aria-hidden="true">{{ row.icon }}</span>
                      <span class="password-hint-label">{{ row.label }}</span>
                    </li>
                  </ul>
                </div>
              </transition>

              <b-form-row class="mt-3">
                <b-col lg="12" class="d-flex justify-content-center">
                  <div ref="turnstile"></div>
                </b-col>
              </b-form-row>
              <b-form-row class="mt-4">
                <b-col lg="12">
                  <button
                    type="submit"
                    class="submit-btn w-100 py-2"
                    :disabled="loading.register || loading.verify"
                  >
                    <span v-if="loading.register" class="spinner"></span>
                    <span class="submit-btn-text">
                      {{ loading.register ? "Создаём аккаунт..." : "Создать аккаунт" }}
                    </span>
                  </button>

                  <!--
                    Regulation §2 Step 1: three independent consents must
                    be recorded — Terms, Privacy Policy, Risk Warning.
                    The user re-confirms them on first login via the
                    PolicyModal (where they actually hit the DB), so the
                    checkboxes here are a pre-acknowledgement during
                    registration and gate the submit button.
                  -->
                  <div class="policy-checkbox-group mt-3" :class="{ invalid: policyError }">
                    <label class="policy-checkbox">
                      <input
                        type="checkbox"
                        v-model="acceptTerms"
                        :disabled="loading.register || loading.verify"
                      />
                      <span class="policy-checkbox-box"></span>
                      <span class="policy-checkbox-label">
                        Я принимаю
                        <a href="/#/terms" class="terms-link" target="_blank" rel="noopener noreferrer">Пользовательское соглашение</a>
                      </span>
                    </label>
                    <label class="policy-checkbox mt-2">
                      <input
                        type="checkbox"
                        v-model="acceptPrivacy"
                        :disabled="loading.register || loading.verify"
                      />
                      <span class="policy-checkbox-box"></span>
                      <span class="policy-checkbox-label">
                        Я принимаю
                        <a href="/#/policy" class="terms-link" target="_blank" rel="noopener noreferrer">Политику конфиденциальности</a>
                      </span>
                    </label>
                    <label class="policy-checkbox mt-2">
                      <input
                        type="checkbox"
                        v-model="acceptRisk"
                        :disabled="loading.register || loading.verify"
                      />
                      <span class="policy-checkbox-box"></span>
                      <span class="policy-checkbox-label">
                        Я ознакомлен(а) с
                        <a href="/#/risk" class="terms-link" target="_blank" rel="noopener noreferrer">Предупреждением о рисках</a>
                      </span>
                    </label>
                  </div>

                  <transition name="fade">
                    <div v-if="policyError" class="policy-error mt-2">
                      <span class="policy-error-icon">!</span>
                      <span>{{ policyError }}</span>
                    </div>
                  </transition>
                </b-col>
              </b-form-row>
            </b-form>
          </div>

          <!-- STEP 2: OTP Verification -->
          <div v-else key="step2">
            <div class="otp-head">
              <div class="otp-title">Введите код</div>
              <div class="otp-subtitle">
                Мы отправили код подтверждения на
                <span class="otp-email">{{ login.username }}</span>
              </div>
            </div>

            <div class="otp-box">
              <label class="reg-label" for="otp">Код из письма</label>

              <input
                id="otp"
                type="text"
                inputmode="numeric"
                maxlength="6"
                class="reg-input w-100 p-2 otp-input"
                v-model.trim="otp"
                :disabled="loading.verify || loading.register"
                placeholder="например: 1234"
                @keyup.enter="verifyOtp"
              />

              <button
                class="submit-btn w-100 py-2 mt-3"
                :disabled="loading.verify || !otp || otp.length < 4"
                @click="verifyOtp"
              >
                <span v-if="loading.verify" class="spinner"></span>
                <span class="submit-btn-text">
                  {{ loading.verify ? "Проверяем..." : "Подтвердить" }}
                </span>
              </button>

              <button
                class="ghost-btn w-100 py-2 mt-2"
                :disabled="loading.verify || loading.register"
                @click="backToRegister"
              >
                Назад
              </button>

              <div class="otp-hint mt-3">
                Не пришёл код? Проверьте папку «Спам».
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { getHeader, apiDomain } from "@/config/config";

export default {
  name: "PersonReg1",
  data() {
    return {
      step: 1, // 1 = регистрация, 2 = otp
      login: {
        username: "",
        password: "",
        passwordRe: "",
      },
      otp: "",
      uiError: "",
      uiErrorList: [],
      uiSuccess: "",
      redirectTimer: null,
      showPassword: false,
      showPasswordRe: false,
      passwordFieldFocused: false,
      passwordBlurTimer: null,
      loading: {
        register: false,
        verify: false,
      },
      // Three separate pre-consents matching Regulation §1.1 / §2 Step 1.
      // The actual DB write happens later via the PolicyModal on first
      // login; here we just gate the registration submit button.
      acceptTerms: false,
      acceptPrivacy: false,
      acceptRisk: false,
      policyError: "",
      turnstileToken: "",
      turnstileWidgetId: null,
    };
  },
  computed: {
    turnstileSiteKey() {
      return process.env.VUE_APP_TURNSTILE_SITE_KEY || "0x4AAAAAAFe9KnHziunj8ygU";
    },
    showPasswordHints() {
      if (this.step !== 1) return false;
      const p = (this.login.password || "").length;
      const p2 = (this.login.passwordRe || "").length;
      return this.passwordFieldFocused || p > 0 || p2 > 0;
    },
    passwordRequirementRows() {
      const password = this.login.password || "";
      const passwordRe = this.login.passwordRe || "";
      const username = (this.login.username || "").trim();
      const started = password.length > 0;
      const pl = password.toLowerCase();
      const ul = username.toLowerCase();

      const row = (key, label, passWhenStarted) => {
        if (!started) {
          return { key, label, icon: "○", stateClass: "hint-pending" };
        }
        const ok = passWhenStarted();
        return {
          key,
          label,
          icon: ok ? "✓" : "✕",
          stateClass: ok ? "hint-ok" : "hint-fail",
        };
      };

      const weakOk = () => {
        if (!["password", "1234", "qwerty", "admin", "letmein", "welcome"].some((w) => pl.includes(w))) {
          return true;
        }
        return false;
      };

      const userRow = () => {
        if (!started) {
          return {
            key: "user",
            label: ul
              ? "Пароль не содержит ваш email"
              : "Пароль не должен повторять email (укажите email выше)",
            icon: "○",
            stateClass: "hint-pending",
          };
        }
        if (!ul) {
          return {
            key: "user",
            label: "Когда укажете email, он не должен входить в пароль",
            icon: "○",
            stateClass: "hint-pending",
          };
        }
        const ok = !pl.includes(ul);
        return {
          key: "user",
          label: "Пароль не содержит ваш email",
          icon: ok ? "✓" : "✕",
          stateClass: ok ? "hint-ok" : "hint-fail",
        };
      };

      const matchRow = () => {
        if (!started) {
          return { key: "match", label: "Пароли совпадают", icon: "○", stateClass: "hint-pending" };
        }
        if (!passwordRe.length) {
          return { key: "match", label: "Пароли совпадают", icon: "○", stateClass: "hint-pending" };
        }
        const ok = password === passwordRe;
        return {
          key: "match",
          label: "Пароли совпадают",
          icon: ok ? "✓" : "✕",
          stateClass: ok ? "hint-ok" : "hint-fail",
        };
      };

      return [
        row("len", "Не менее 8 символов", () => password.length >= 8),
        row("upper", "Хотя бы одна заглавная буква (A–Z)", () => /[A-Z]/.test(password)),
        row("lower", "Хотя бы одна строчная буква (a–z)", () => /[a-z]/.test(password)),
        row("digit", "Хотя бы одна цифра", () => /[0-9]/.test(password)),
        row(
          "special",
          "Хотя бы один спецсимвол (!@#$%^&* и др.)",
          () => /[!@#$%^&*(),.?":{}|<>]/.test(password)
        ),
        row("space", "Без пробелов", () => !/\s/.test(password)),
        userRow(),
        row(
          "weak",
          "Без простых шаблонов (password, 1234, qwerty, admin…)",
          weakOk
        ),
        matchRow(),
      ];
    },
  },
  watch: {
    acceptTerms(val) {
      if (val) this.policyError = "";
    },
    acceptPrivacy(val) {
      if (val) this.policyError = "";
    },
    acceptRisk(val) {
      if (val) this.policyError = "";
    },
    "login.password"() {
      if (this.uiErrorList.length) this.uiErrorList = [];
      if (this.uiError && this.uiError.includes("Парол")) this.uiError = "";
    },
    "login.passwordRe"() {
      if (this.uiErrorList.length) this.uiErrorList = [];
      if (this.uiError && this.uiError.includes("Парол")) this.uiError = "";
    },
    step(val) {
      // Перерисовываем Turnstile при возврате на шаг регистрации.
      if (val === 1) this.$nextTick(() => this.renderTurnstile());
    },
  },
  mounted() {
    this.$nextTick(() => this.renderTurnstile());
  },
  beforeDestroy() {
    if (this.redirectTimer) clearTimeout(this.redirectTimer);
    if (this.passwordBlurTimer) clearTimeout(this.passwordBlurTimer);
  },
  methods: {
    loadTurnstileScript() {
      return new Promise((resolve) => {
        if (window.turnstile) { resolve(); return; }
        const existing = document.getElementById("cf-turnstile-script");
        if (existing) { existing.addEventListener("load", () => resolve()); return; }
        const s = document.createElement("script");
        s.id = "cf-turnstile-script";
        s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
        s.async = true; s.defer = true;
        s.onload = () => resolve();
        document.head.appendChild(s);
      });
    },
    renderTurnstile() {
      this.loadTurnstileScript().then(() => {
        const el = this.$refs.turnstile;
        if (!el || !window.turnstile) return;
        if (this.turnstileWidgetId !== null) return;
        this.turnstileWidgetId = window.turnstile.render(el, {
          sitekey: this.turnstileSiteKey,
          callback: (token) => { this.turnstileToken = token; },
          "error-callback": () => { this.turnstileToken = ""; },
          "expired-callback": () => { this.turnstileToken = ""; },
        });
      });
    },
    resetTurnstile() {
      this.turnstileToken = "";
      if (window.turnstile && this.turnstileWidgetId !== null) {
        try { window.turnstile.reset(this.turnstileWidgetId); } catch (e) { /* noop */ }
      }
    },
    onPasswordFieldFocus() {
      if (this.passwordBlurTimer) clearTimeout(this.passwordBlurTimer);
      this.passwordFieldFocused = true;
    },
    onPasswordFieldBlur() {
      this.passwordBlurTimer = setTimeout(() => {
        this.passwordFieldFocused = false;
      }, 180);
    },
    onPasswordInput() {
      if (this.uiErrorList.length) this.uiErrorList = [];
    },
    clearMessages() {
      this.uiError = "";
      this.uiErrorList = [];
      this.uiSuccess = "";
      this.policyError = "";
    },

    collectPasswordFailures(username, password, passwordRe) {
      const failures = [];
      const u = (username || "").trim();
      const pl = (password || "").toLowerCase();
      if (password !== passwordRe) failures.push("Пароли должны совпадать.");
      if ((password || "").length < 8) failures.push("Пароль должен быть не менее 8 символов.");
      if (!/[A-Z]/.test(password || "")) failures.push("Нужна хотя бы одна заглавная буква (A–Z).");
      if (!/[a-z]/.test(password || "")) failures.push("Нужна хотя бы одна строчная буква (a–z).");
      if (!/[0-9]/.test(password || "")) failures.push("Нужна хотя бы одна цифра.");
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password || "")) {
        failures.push("Нужен хотя бы один специальный символ (!@#$%^&* и др.).");
      }
      if (/\s/.test(password || "")) failures.push("Пароль не должен содержать пробелов.");
      if (u && pl.includes(u.toLowerCase())) failures.push("Пароль не должен содержать ваш email.");
      const weakWords = ["password", "1234", "qwerty", "admin", "letmein", "welcome"];
      if (weakWords.some((w) => pl.includes(w))) {
        failures.push(
          "Уберите предсказуемые фрагменты: password, 1234, qwerty, admin, letmein, welcome."
        );
      }
      return failures;
    },

    async handleRegister() {
      this.clearMessages();

      if (!this.acceptTerms || !this.acceptPrivacy || !this.acceptRisk) {
        this.policyError = "Необходимо принять все три документа: соглашение, политику и предупреждение о рисках.";
        this.toastRegisterError(this.policyError);
        return;
      }

      const loginUrl = apiDomain + "/reg1";

      const username = (this.login.username || "").trim();
      const password = this.login.password;
      const passwordRe = this.login.passwordRe;

      const policyFailures = this.collectPasswordFailures(username, password, passwordRe);
      if (policyFailures.length) {
        this.uiErrorList = policyFailures;
        this.uiError = "";
        return;
      }

      this.loading.register = true;

      try {
        const res = await axios.post(
          loginUrl,
          { username, password },
          { headers: { ...(getHeader() || {}), "Cf-Turnstile-Response": this.turnstileToken } }
        );

        const ok =
          res.status === 200 &&
          (res.data === "success" ||
            res.data === "SUCCESS" ||
            (res.data && res.data.status === "success"));

        if (!ok) {
          const msg = "Не удалось зарегистрироваться. Попробуйте ещё раз.";
          this.uiError = msg;
          this.toastRegisterError(msg);
          return;
        }

        this.uiSuccess = "Аккаунт создан. Мы отправили код подтверждения на вашу почту.";
        this.step = 2;
        this.otp = "";
      } catch (e) {
        const status = e && e.response && e.response.status;
        const internalErr =
          e && e.response && e.response.headers && e.response.headers["internalerror"];
        const bodyStr =
          e && e.response && typeof e.response.data === "string"
            ? e.response.data.trim()
            : "";

        // 403 — не пройдена проверка Turnstile (анти-бот).
        if (status === 403) {
          const msg = "Проверка «я не робот» не пройдена. Обновите страницу и попробуйте снова.";
          this.uiError = msg;
          this.toastRegisterError(msg);
          this.resetTurnstile();
          return;
        }

        // 409 + internalerror=email-already-registered — owner already
        // confirmed the OTP for this email. Re-registration is rejected;
        // prompt them to use forgot-password instead.
        // Body fallback covers CORS setups that strip the custom header.
        if (
          status === 409 &&
          (internalErr === "email-already-registered" ||
            bodyStr === "email-already-registered")
        ) {
          const msg = this.$t("regForm.emailAlreadyRegistered");
          this.uiError = msg;
          this.toastRegisterError(msg);
          return;
        }

        const serverMsg = this.extractApiErrorMessage(e);
        const msg =
          serverMsg ||
          "Не удалось зарегистрироваться. Проверьте данные и попробуйте снова.";
        this.uiError = serverMsg ? serverMsg : msg;
        this.toastRegisterError(msg);
      } finally {
        this.loading.register = false;
        // Токен одноразовый: если не ушли на шаг OTP — сбросим виджет.
        if (this.step !== 2) this.resetTurnstile();
      }
    },

    async verifyOtp() {
      this.clearMessages();

      const verifyUrl = apiDomain + "/exchange/open/otp/verify";
      const email = (this.login.username || "").trim();
      const otp = (this.otp || "").trim();

      if (!email) {
        this.uiError = "Email не указан.";
        this.toastRegisterError(this.uiError);
        return;
      }
      if (!otp || otp.length < 4) {
        this.uiError = "Введите код из письма.";
        this.toastRegisterError(this.uiError);
        return;
      }

      this.loading.verify = true;

      try {
        const res = await axios.post(
          verifyUrl,
          { email, otp },
          { headers: getHeader() }
        );

        const ok =
          res.status === 200 &&
          (res.data === "success" ||
            res.data === "SUCCESS" ||
            (res.data && res.data.status === "success"));

        if (!ok) {
          this.uiError = "Неверный код. Проверьте и попробуйте снова.";
          this.toastRegisterError(this.uiError);
          return;
        }

        this.uiSuccess = "Код подтверждён ✅ Почта успешно подтверждена. Сейчас перенаправим на вход...";
        this.uiError = "";

        this.redirectTimer = setTimeout(() => {
          this.$router.push("/login");
        }, 1200);
      } catch (e) {
        this.uiError = "Неверный код. Проверьте и попробуйте снова.";
        this.toastRegisterError(this.uiError);
      } finally {
        this.loading.verify = false;
      }
    },

    backToRegister() {
      this.clearMessages();
      this.step = 1;
      this.otp = "";
    },

    toastRegisterError(message) {
      if (!message || !this.$bvToast) return;
      this.$bvToast.toast(message, {
        title: "Регистрация",
        variant: "danger",
        autoHideDelay: 6500,
        toaster: "b-toaster-top-center",
        solid: true,
      });
    },

    extractApiErrorMessage(err) {
      const d = err && err.response && err.response.data;
      if (d == null) return "";
      if (typeof d === "string") return d.trim() || "";
      if (typeof d.message === "string") return d.message.trim() || "";
      if (typeof d.error === "string") return d.error.trim() || "";
      if (Array.isArray(d.errors) && d.errors.length) {
        return String(d.errors[0]).trim() || "";
      }
      return "";
    },
  },
};
</script>

<style scoped>
/* Background */
.login-right {
  background: var(--Neutrals-1, #141416);
  min-height: 110vh;
}

/* Layout */
.reg-form {
  display: block;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  padding: 0 14px;
}

/* Card */
.auth-card {
  border-radius: 18px;
  padding: 22px 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(35, 38, 47, 0.7);
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(10px);
}

/* Brand */
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.brand-dot {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: var(--Primary-1, #EFB914);
  box-shadow: 0 0 0 6px rgba(239, 185, 20, 0.16);
}
.brand-title {
  color: var(--Neutrals-8, #FCFCFD);
  font-family: Inter;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.2px;
}

/* Steps */
.steps {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 14px 0 18px;
}
.step {
  display: flex;
  align-items: center;
  gap: 10px;
  opacity: 0.75;
}
.step.active,
.step.done {
  opacity: 1;
}
.step-badge {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  font-family: Inter;
  font-weight: 800;
  font-size: 13px;
  color: #141416;
  background: rgba(239, 185, 20, 0.45);
  border: 1px solid rgba(239, 185, 20, 0.6);
}
.step.active .step-badge {
  background: var(--Primary-1, #EFB914);
  border-color: var(--Primary-1, #EFB914);
}
.step.done .step-badge {
  background: rgba(77, 205, 128, 0.9);
  border-color: rgba(77, 205, 128, 0.9);
}
.step-text {
  color: var(--Neutrals-8, #FCFCFD);
  font-family: Inter;
  font-size: 13px;
  font-weight: 700;
}
.step-line {
  flex: 1;
  height: 2px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
}
.step-line.active {
  background: rgba(239, 185, 20, 0.7);
}

/* Labels / Inputs */
.reg-label {
  color: var(--Neutrals-5, #B1B5C3);
  font-family: Inter;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.6px;
  line-height: 12px;
  text-transform: uppercase;
}
.password-wrapper {
  position: relative;
}
.password-wrapper .reg-input {
  padding-right: 2.5rem !important;
}
.password-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
}
.password-toggle:hover {
  color: rgba(255, 255, 255, 0.9);
}
.reg-input {
  color: var(--Neutrals-8, #FCFCFD);
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  background-color: rgba(20, 20, 22, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  outline: none;
  border-radius: 14px;
  transition: 0.18s ease;
}
.reg-input:focus {
  border-color: rgba(239, 185, 20, 0.55);
  box-shadow: 0 0 0 4px rgba(239, 185, 20, 0.12);
}
.reg-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Buttons */
.submit-btn {
  appearance: none;
  cursor: pointer;
  border-radius: 14px;
  border: 1px solid var(--Primary-1, #EFB914);
  background: linear-gradient(180deg, rgba(239, 185, 20, 1), rgba(239, 185, 20, 0.78));
  transition: transform 0.12s ease, filter 0.12s ease;
}
.submit-btn:hover {
  filter: brightness(1.03);
}
.submit-btn:active {
  transform: translateY(1px);
}
.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.submit-btn-text {
  color: #141416;
  font-family: Inter;
  font-size: 15px;
  font-weight: 900;
  line-height: 16px;
}

.ghost-btn {
  appearance: none;
  cursor: pointer;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.06);
  color: var(--Neutrals-8, #FCFCFD);
  font-family: Inter;
  font-size: 14px;
  font-weight: 800;
  transition: 0.12s ease;
}
.ghost-btn:hover {
  background: rgba(255, 255, 255, 0.08);
}
.ghost-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Terms */
.terms-text {
  font-family: Inter;
  font-size: 12px;
  opacity: 0.9;
}
.terms-link {
  color: var(--Primary-1, #EFB914);
  font-weight: 800;
  text-decoration: none;
}
.terms-link:hover {
  text-decoration: underline;
}

/* Policy checkbox */
.policy-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  font-family: Inter;
  font-size: 13px;
  font-weight: 600;
  color: var(--Neutrals-6, #B1B5C3);
  transition: color 0.18s ease;
}
.policy-checkbox:hover {
  color: var(--Neutrals-7, #E6E8EC);
}
.policy-checkbox input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.policy-checkbox-box {
  position: relative;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 8px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  background: rgba(20, 20, 22, 0.5);
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}
.policy-checkbox input:checked + .policy-checkbox-box {
  border-color: var(--Primary-1, #EFB914);
  background: rgba(239, 185, 20, 0.25);
  box-shadow: inset 0 0 0 2px rgba(239, 185, 20, 0.3);
}
.policy-checkbox input:checked + .policy-checkbox-box::after {
  content: "";
  position: absolute;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid var(--Primary-1, #EFB914);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.policy-checkbox.invalid .policy-checkbox-box {
  border-color: rgba(255, 77, 77, 0.7);
  background: rgba(255, 77, 77, 0.08);
}
.policy-checkbox-label {
  line-height: 1.3;
}

.policy-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  font-family: Inter;
  font-size: 13px;
  font-weight: 600;
  color: #ffd6d6;
  background: rgba(255, 77, 77, 0.12);
  border: 1px solid rgba(255, 77, 77, 0.25);
  animation: policyShake 0.4s ease;
}
.policy-error-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 77, 77, 0.9);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
}
@keyframes policyShake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}

/* Alerts */
.alert-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  margin: 10px 0 2px;
  font-family: Inter;
  font-size: 13px;
  font-weight: 700;
}
.alert-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
}
.alert-error {
  color: #ffd6d6;
  background: rgba(255, 77, 77, 0.12);
  border: 1px solid rgba(255, 77, 77, 0.22);
}
.alert-error .alert-dot {
  background: rgba(255, 77, 77, 0.95);
}
.alert-success {
  color: #d6ffe6;
  background: rgba(77, 205, 128, 0.12);
  border: 1px solid rgba(77, 205, 128, 0.22);
  box-shadow: 0 10px 30px rgba(77, 205, 128, 0.1);
}
.alert-success .alert-dot {
  background: rgba(77, 205, 128, 0.95);
}

.alert-box-rich {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.alert-rich-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.alert-rich-title {
  font-weight: 800;
  letter-spacing: -0.02em;
}
.alert-error-list {
  margin: 0;
  padding-left: 1.35rem;
  color: #ffe8e8;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.45;
}
.alert-error-list li {
  margin-bottom: 4px;
}
.alert-error-list li:last-child {
  margin-bottom: 0;
}

.reg-input-active-hint {
  border-color: rgba(239, 185, 20, 0.35) !important;
  background-color: rgba(239, 185, 20, 0.06);
}

.password-hints {
  margin-top: 14px;
  padding: 14px 14px 12px;
  border-radius: 14px;
  background: rgba(20, 22, 30, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}
.password-hints-title {
  font-family: Inter;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(177, 181, 195, 0.95);
  margin-bottom: 10px;
}
.password-hints-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.password-hint-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-family: Inter;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  padding: 5px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  color: rgba(230, 232, 236, 0.88);
}
.password-hint-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.password-hint-icon {
  flex-shrink: 0;
  width: 18px;
  text-align: center;
  font-size: 13px;
  font-weight: 800;
}
.hint-pending .password-hint-icon {
  color: rgba(119, 126, 144, 0.95);
}
.hint-pending .password-hint-label {
  color: rgba(177, 181, 195, 0.9);
}
.hint-ok .password-hint-icon {
  color: #58bd7d;
}
.hint-ok .password-hint-label {
  color: rgba(230, 232, 236, 0.95);
}
.hint-fail .password-hint-icon {
  color: #ff6b6b;
}
.hint-fail .password-hint-label {
  color: #ffc9c9;
}

/* OTP */
.otp-head {
  margin-top: 6px;
}
.otp-title {
  color: var(--Neutrals-8, #FCFCFD);
  font-family: Inter;
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.4px;
}
.otp-subtitle {
  color: var(--Neutrals-4, #777E90);
  font-family: Inter;
  font-size: 13px;
  font-weight: 600;
  margin-top: 6px;
}
.otp-email {
  color: var(--Primary-1, #EFB914);
  font-weight: 900;
}
.otp-box {
  margin-top: 14px;
}
.otp-input {
  letter-spacing: 2px;
  font-weight: 900;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 2px solid rgba(20, 20, 22, 0.35);
  border-top-color: rgba(20, 20, 22, 0.95);
  margin-right: 8px;
  vertical-align: -3px;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.slide-fade-enter-active {
  transition: all 0.22s ease;
}
.slide-fade-leave-active {
  transition: all 0.16s ease;
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(6px);
  opacity: 0;
}
</style>
